/**
 * A drag held over the swap control of a paged bar (P2.8-T30, ADR-121 decisions 3.1 and 8): held there for
 * `SWAP_HOLD_MS` it shows the other page, so a drag from the bar or the bag reaches a slot the page did not show. One
 * hold shows the other page once: the drag must leave the control and return to turn it again, so a pointer resting
 * on the control never flips the bar back and forth under the hand.
 */

/**
 * How long a drag rests on the swap control before the other page shows, in milliseconds.
 */
export const SWAP_HOLD_MS = 500;

/**
 * The swap control as the bar draws it (`hud/bar-swap.tsx`).
 */
const SWAP_CONTROL = "[data-hud='ability-swap']";

/**
 * The timers a hold counts on.
 */
export interface HoldTimers {
  readonly set: (callback: () => void, delayMs: number) => unknown;
  readonly clear: (handle: unknown) => void;
}

/**
 * The page's own timers.
 */
const PAGE_TIMERS: HoldTimers = {
  set: (callback, delayMs) => setTimeout(callback, delayMs),
  clear: (handle) => clearTimeout(handle as ReturnType<typeof setTimeout>),
};

/**
 * Whether a point of the page is over the swap control.
 *
 * @param x Client x in CSS pixels.
 * @param y Client y in CSS pixels.
 * @param root The document to look in.
 * @returns True over the control; false where the bar shows all ten, which draws none.
 */
export function overSwapControl(x: number, y: number, root: Pick<Document, "elementFromPoint"> = document): boolean {
  const under = root.elementFromPoint(x, y);
  return under !== null && under.closest(SWAP_CONTROL) !== null;
}

/**
 * One drag's hold on the swap control.
 *
 * @example
 * ```ts
 * const hold = new SwapHold(() => arranging.follow.turnForDrag());
 * hold.over(overSwapControl(event.clientX, event.clientY));
 * hold.end();
 * ```
 */
export class SwapHold {
  private timer: unknown = null;
  private resting = false;

  /**
   * @param swap Shows the other page.
   * @param timers The timers, the page's own unless given.
   */
  public constructor(
    private readonly swap: () => unknown,
    private readonly timers: HoldTimers = PAGE_TIMERS,
  ) {}

  /**
   * Tells the hold where the drag is, at each move: arriving on the control starts the count, leaving it stops the
   * count, and staying changes nothing.
   *
   * @param on Whether the drag is over the swap control.
   */
  public over(on: boolean): void {
    if (on === this.resting) {
      return;
    }
    this.resting = on;
    this.timers.clear(this.timer);
    this.timer = null;
    if (on) {
      this.timer = this.timers.set(() => {
        this.timer = null;
        this.swap();
      }, SWAP_HOLD_MS);
    }
  }

  /**
   * Ends the hold as its drag ends, dropped or cancelled.
   */
  public end(): void {
    this.over(false);
  }
}
